import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { PaDrawer } from "components/platform-admin";
import { EMAIL_REGEX_EXP, REGEX_PHONE_NUMBER } from "Constants/constant";
import { createPlatformSaleLead } from "store/services/platformAdmin.services";

const SERVICE_OPTIONS = [
  "Plan Management",
  "Support Coordination",
  "Technical Skills",
  "Others",
];

const defaultValues = {
  name: "",
  email: "",
  phone: "",
  abn: "",
  asn: "",
  services: {},
};

const CreateProspectDrawer = ({ open, onClose, onCreated }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const onSubmit = async (data) => {
    const selectedServices = SERVICE_OPTIONS.reduce((acc, service, index) => {
      if (data.services?.[index]?.[service]) {
        acc.push(service);
      }
      return acc;
    }, []);

    if (selectedServices.length === 0) {
      toast.error("Select at least one service");
      return;
    }

    setSubmitting(true);

    try {
      await createPlatformSaleLead({
        name: data.name.trim(),
        email: data.email.trim(),
        phone: data.phone,
        abn: data.abn,
        asn: data.asn,
        services: selectedServices,
      });
      toast.success("Prospect invited successfully");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || "Failed to create prospect");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PaDrawer
      open={open}
      onClose={onClose}
      title="Invite prospect"
      description="Send a signup invitation to a new service provider."
      widthClassName="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-prospect-form"
            disabled={submitting}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Send invite"}
          </button>
        </div>
      }
    >
      <form id="create-prospect-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Business name</label>
            <input
              {...register("name", { required: "Business name is required", maxLength: 70 })}
              type="text"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {errors.name ? (
              <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              {...register("email", {
                required: "Email is required",
                pattern: { value: EMAIL_REGEX_EXP, message: "Enter a valid email" },
              })}
              type="email"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {errors.email ? (
              <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Phone</label>
            <input
              {...register("phone", {
                required: "Phone is required",
                pattern: { value: REGEX_PHONE_NUMBER, message: "Enter a valid phone number" },
                onChange: (event) => {
                  event.target.value = event.target.value.replace(/\D/g, "").slice(0, 10);
                },
              })}
              type="tel"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {errors.phone ? (
              <p className="mt-1 text-xs text-red-600">{errors.phone.message}</p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">ABN</label>
            <input
              {...register("abn", {
                required: "ABN is required",
                minLength: { value: 11, message: "ABN must be 11 digits" },
                maxLength: { value: 11, message: "ABN must be 11 digits" },
                onChange: (event) => {
                  event.target.value = event.target.value.replace(/\D/g, "").slice(0, 11);
                },
              })}
              inputMode="numeric"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {errors.abn ? (
              <p className="mt-1 text-xs text-red-600">{errors.abn.message}</p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">ACN</label>
            <input
              {...register("asn", {
                required: "ACN is required",
                onChange: (event) => {
                  event.target.value = event.target.value.replace(/\D/g, "");
                },
              })}
              inputMode="numeric"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {errors.asn ? (
              <p className="mt-1 text-xs text-red-600">{errors.asn.message}</p>
            ) : null}
          </div>
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-gray-700">Services</legend>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {SERVICE_OPTIONS.map((service, index) => (
              <label key={service} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  {...register(`services.${index}.${service}`)}
                  type="checkbox"
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                {service}
              </label>
            ))}
          </div>
        </fieldset>
      </form>
    </PaDrawer>
  );
};

export default CreateProspectDrawer;
